import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const POLL_MS = 2000;
// A healthy job reports progress continuously, so we only give up when it goes quiet.
// Heavy sets (8 sheets + AI detailing) legitimately run for many minutes.
const STALL_MS = 420000;
const MAX_POLL_ERRORS = 15;

// Tracks async drawing-generation jobs (POST /drawings/{pid}/generate → poll status).
// Jobs run server-side (FreeCAD/CadQuery can take 40-120s), so the UI never holds
// a synchronous request open past the ingress timeout.
export default function useDrawingJob(projectId, { onTerminal } = {}) {
  const [jobs, setJobs] = useState({});
  const pollRefs = useRef({});
  const terminalCb = useRef(onTerminal);
  terminalCb.current = onTerminal;

  const updateJob = useCallback((jobId, updates) => {
    setJobs((prev) => ({ ...prev, [jobId]: { ...prev[jobId], ...updates } }));
  }, []);

  const stopPolling = useCallback((jobId) => {
    const rec = pollRefs.current[jobId];
    if (rec) {
      clearInterval(rec.interval);
      delete pollRefs.current[jobId];
    }
  }, []);

  const startPolling = useCallback((jobId) => {
    if (pollRefs.current[jobId]) return;
    const rec = { interval: null, lastChange: Date.now(), signature: null, errors: 0, terminalFired: false };
    pollRefs.current[jobId] = rec;
    rec.interval = setInterval(async () => {
      if (pollRefs.current[jobId] !== rec) return; // stopped while a tick was in flight
      if (Date.now() - rec.lastChange > STALL_MS) {
        stopPolling(jobId);
        updateJob(jobId, {
          status: "failed",
          error: { message: "The job stopped reporting progress — it may still be running on the server.",
                   recoverable: true, suggestion: "Reload the page to re-attach to the running job." },
        });
        return;
      }
      try {
        const { data } = await api.get(`/drawings/${projectId}/job/${jobId}`);
        if (pollRefs.current[jobId] !== rec) return; // cancelled/dismissed mid-flight
        rec.errors = 0;
        // Any movement — percent, step text or a sheet changing state — resets the stall clock.
        const sig = JSON.stringify([data.status, data.progress?.percent, data.progress?.current_step,
                                    data.progress?.steps_completed, data.progress?.drawing_statuses]);
        if (sig !== rec.signature) {
          rec.signature = sig;
          rec.lastChange = Date.now();
        }
        updateJob(jobId, data);
        if (["done", "failed", "cancelled"].includes(data.status) && !rec.terminalFired) {
          rec.terminalFired = true; // fire the completion callback exactly once
          stopPolling(jobId);
          terminalCb.current?.(data);
        }
      } catch (e) {
        rec.errors += 1;
        console.warn("drawing job poll failed", e?.message);
        if (rec.errors >= MAX_POLL_ERRORS) {
          stopPolling(jobId);
          updateJob(jobId, {
            status: "failed",
            error: { message: "Lost contact with the server while the job was running.",
                     recoverable: true, suggestion: "Reload the page to re-attach to the running job." },
          });
        }
      }
    }, POLL_MS);
  }, [projectId, stopPolling, updateJob]);

  // Re-attach to in-flight jobs when (re)entering the page — jobs survive navigation.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/drawings/${projectId}/jobs`, { params: { status: "pending,running" } });
        if (!alive) return;
        (data.jobs || []).forEach((j) => {
          updateJob(j.job_id, { ...j, tracked_at: Date.now() });
          startPolling(j.job_id);
        });
      } catch { /* no active jobs */ }
    })();
    return () => {
      alive = false;
      Object.keys(pollRefs.current).forEach((id) => stopPolling(id));
    };
  }, [projectId, startPolling, stopPolling, updateJob]);

  const generate = useCallback(async (jobType, drawingType = null, config = {}) => {
    const { data } = await api.post(`/drawings/${projectId}/generate`, {
      job_type: jobType, drawing_type: drawingType, config,
    });
    updateJob(data.job_id, {
      job_id: data.job_id, status: "pending", job_type: jobType, drawing_type: drawingType,
      progress: { current_step: "Queued — waiting to start…", percent: 0 }, tracked_at: Date.now(),
    });
    startPolling(data.job_id);
    return data.job_id;
  }, [projectId, startPolling, updateJob]);

  const cancel = useCallback(async (jobId) => {
    stopPolling(jobId);
    updateJob(jobId, { status: "cancelled" });
    try {
      await api.post(`/drawings/${projectId}/job/${jobId}/cancel`);
    } catch (e) { console.warn("cancel failed", e?.message); }
  }, [projectId, stopPolling, updateJob]);

  const retry = useCallback(async (jobId) => {
    const { data } = await api.post(`/drawings/${projectId}/job/${jobId}/retry`);
    setJobs((prev) => { const n = { ...prev }; delete n[jobId]; return n; });
    updateJob(data.job_id, {
      job_id: data.job_id, status: "pending",
      progress: { current_step: "Queued (retry)…", percent: 0 }, tracked_at: Date.now(),
    });
    startPolling(data.job_id);
    return data.job_id;
  }, [projectId, startPolling, updateJob]);

  const prioritise = useCallback(async (jobId) => {
    const { data } = await api.post(`/drawings/${projectId}/job/${jobId}/prioritise`);
    return data;
  }, [projectId]);

  const dismiss = useCallback((jobId) => {
    stopPolling(jobId);
    setJobs((prev) => { const n = { ...prev }; delete n[jobId]; return n; });
  }, [stopPolling]);

  const jobList = Object.values(jobs).sort((a, b) => (b.tracked_at || 0) - (a.tracked_at || 0));
  const activeJobs = jobList.filter((j) => ["pending", "running"].includes(j.status));

  return { jobs, jobList, activeJobs, isGenerating: activeJobs.length > 0, generate, cancel, retry, dismiss, prioritise };
}
